import { useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';

export interface DevResponseSummary {
  filesToCreate: string[];
  filesToModify: string[];
  commandsToRun: string[];
  testsToWrite: string[];
  openQuestions: string[];
}

interface SummaryCardProps {
  summary: DevResponseSummary;
  issueId?: string | null;
  artefactId?: string | null;
}

const SECTIONS: { key: keyof DevResponseSummary; label: string; icon: string }[] = [
  { key: 'filesToCreate', label: 'Files to Create', icon: '📄' },
  { key: 'filesToModify', label: 'Files to Modify', icon: '✏️' },
  { key: 'commandsToRun', label: 'Commands to Run', icon: '▶' },
  { key: 'testsToWrite', label: 'Tests to Write', icon: '🧪' },
  { key: 'openQuestions', label: 'Open Questions', icon: '❓' },
];

export default function SummaryCard({ summary, issueId, artefactId }: SummaryCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [markingInProgress, setMarkingInProgress] = useState(false);
  const [markedDone, setMarkedDone] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);

  const totalItems = SECTIONS.reduce((acc, s) => acc + summary[s.key].length, 0);

  const handleMarkInProgress = async () => {
    if (!issueId) return;
    setMarkingInProgress(true);
    setMarkError(null);
    try {
      await apiFetch(`/issues/${issueId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'IN_PROGRESS' }),
      });
      setMarkedDone(true);
    } catch (err) {
      // #3055 — show the server's reason (e.g. #2948's membership gate); the
      // button stays enabled for retry.
      setMarkError(err instanceof ApiError && err.message ? err.message : 'Failed to update issue status');
    } finally {
      setMarkingInProgress(false);
    }
  };

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
      background: 'var(--surface)',
      marginTop: 12,
    }}>
      {/* Collapsed header */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer',
          fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>📋</span>
          <span>Implementation Summary</span>
          <span style={{
            fontSize: 11, fontWeight: 500, padding: '1px 8px', borderRadius: 10,
            background: 'rgba(99,102,241,0.1)', color: 'var(--primary)',
          }}>
            {totalItems} item{totalItems !== 1 ? 's' : ''}
          </span>
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}>
          ▼
        </span>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid var(--border)' }}>
          {SECTIONS.map(({ key, label, icon }) => {
            const items = summary[key];
            if (items.length === 0) return null;
            const isMono = key === 'filesToCreate' || key === 'filesToModify' || key === 'commandsToRun';
            return (
              <div key={key} style={{ marginTop: 12 }}>
                <div style={{
                  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                  color: 'var(--text-secondary)', marginBottom: 6,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span>{icon}</span>
                  <span>{label}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 500, padding: '0 6px', borderRadius: 8,
                    background: 'var(--bg)', color: 'var(--text-secondary)',
                  }}>
                    {items.length}
                  </span>
                </div>
                <ul style={{ margin: 0, paddingLeft: 24, listStyle: 'disc' }}>
                  {items.map((item, i) => (
                    <li key={i} style={{
                      fontFamily: isMono ? 'var(--font-mono)' : 'var(--font-body)',
                      fontSize: 12, color: 'var(--text)', lineHeight: 1.6,
                    }}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* Action buttons */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginTop: 16,
            paddingTop: 12, borderTop: '1px solid var(--border)',
          }}>
            {issueId && !markedDone && (
              <button
                onClick={() => void handleMarkInProgress()}
                disabled={markingInProgress}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                  padding: '6px 14px', borderRadius: 'var(--radius-sm)',
                  border: 'none', background: 'var(--primary)', color: '#fff',
                  cursor: markingInProgress ? 'not-allowed' : 'pointer',
                  opacity: markingInProgress ? 0.6 : 1,
                }}
              >
                {markingInProgress ? 'Updating...' : 'Mark In Progress'}
              </button>
            )}
            {markError && (
              <span role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)' }}>
                {markError}
              </span>
            )}
            {markedDone && (
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--success)', fontWeight: 600 }}>
                ✓ Marked In Progress
              </span>
            )}
            {artefactId && (
              <a
                href={`/artefacts?tabs=${artefactId}&active=${artefactId}`}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                  color: 'var(--primary)', textDecoration: 'none',
                }}
              >
                View Full Response →
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
